"use client";

import { usePathname } from "next/navigation";
import Link from "next/link";
import { useState } from "react";

const languages = [
  { code: "en", flag: "🇺🇸", native: "English" },
  { code: "de", flag: "🇩🇪", native: "Deutsch" },
  { code: "tr", flag: "🇹🇷", native: "Türkçe" },
  { code: "ar", flag: "🇸🇦", native: "العربية" },
];

export default function LanguageSwitcher({ locale }: { locale: string }) {
  const pathname = usePathname();
  const [open, setOpen] = useState(false);

  const current = languages.find((l) => l.code === locale) || languages[0];

  function switchedPath(target: string) {
    const segments = pathname.split("/");
    segments[1] = target;
    return segments.join("/");
  }

  return (
    <div style={{ position: "relative" }}>
      <button
        onClick={() => setOpen(!open)}
        className="bp-mono-sm"
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          color: "var(--bp-ink)",
          background: "none",
          border: 0,
          cursor: "pointer",
        }}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span aria-hidden="true">{current.flag}</span>
        <span>{current.code.toUpperCase()}</span>
        <span aria-hidden="true" style={{ fontSize: 9 }}>{open ? "▲" : "▼"}</span>
      </button>

      {open && (
        <>
          <div
            onClick={() => setOpen(false)}
            aria-hidden="true"
            style={{ position: "fixed", inset: 0, zIndex: 40 }}
          />
          <div
            role="listbox"
            style={{
              position: "absolute",
              right: 0,
              top: "100%",
              marginTop: 8,
              zIndex: 50,
              background: "var(--bp-bg)",
              border: "1px solid var(--bp-ink)",
              minWidth: 200,
              maxHeight: 380,
              overflowY: "auto",
              boxShadow: "0 8px 32px oklch(0.1 0 0 / 0.18)",
            }}
          >
            {languages.map((lang) => {
              const active = lang.code === locale;
              return (
                <Link
                  key={lang.code}
                  href={switchedPath(lang.code)}
                  onClick={() => setOpen(false)}
                  style={{
                    display: "grid",
                    gridTemplateColumns: "20px 1fr auto",
                    gap: 12,
                    alignItems: "center",
                    padding: "10px 14px",
                    fontFamily: "var(--bp-mono)",
                    fontSize: 11,
                    letterSpacing: "0.08em",
                    textTransform: "uppercase",
                    color: active ? "var(--bp-bg)" : "var(--bp-ink)",
                    background: active ? "var(--bp-ink)" : "transparent",
                  }}
                >
                  <span style={{ fontSize: 14 }} aria-hidden="true">
                    {lang.flag}
                  </span>
                  <span style={{ textTransform: "none", letterSpacing: 0, fontFamily: "var(--bp-sans)", fontSize: 13 }}>
                    {lang.native}
                  </span>
                  <span style={{ color: active ? "var(--bp-bg)" : "var(--bp-ink-3)" }}>
                    {lang.code.toUpperCase()}
                  </span>
                </Link>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
